/**
 * client/src/lib/use-recovery-freeze-offer.ts
 *
 * #1239 Task 8 [AC-REC-08] — VICTIM FREEZE listener.
 *
 * Opens an authenticated user-level WebSocket and listens for the
 * `recovery:freeze_offer` event the server broadcasts to EVERY live session of
 * a recovery subject the instant a guardian recovery ceremony is initiated
 * (server/logic/recovery/guardian-ceremony.ts → initiateGuardianRecovery).
 *
 * If an attacker initiates a recovery against a victim who still holds a device,
 * the victim's live session surfaces a one-tap "Freeze" (RecoveryFreezeBanner)
 * that aborts the pending ceremony — a load-bearing REAL-TIME control. The freeze
 * itself (POST /api/recovery/freeze) is owned by the banner; this hook only
 * delivers the offer and exposes a dismiss.
 *
 * Auth handshake mirrors GuardianRecoveryFlow: { type:"auth", userId, sessionId }.
 * The server keys its connection registry by userId; the sessionId here is the
 * app session (any non-empty token works — the registry only uses userId).
 */

import { useEffect, useRef, useState, useCallback } from "react";

export interface FreezeOffer {
  /** The pending recovery session to freeze. */
  sessionId: string;
  /** When the recovery request started (ISO UTC). */
  startedAt?: string;
  /** When the ceremony window expires (ISO UTC). */
  expiresAt?: string;
}

interface UseRecoveryFreezeOfferResult {
  /** The active freeze offer, or null when none is pending / dismissed. */
  readonly offer: FreezeOffer | null;
  /** Clear the current offer (after a successful freeze or an explicit dismiss). */
  readonly clear: () => void;
}

/**
 * Listen for `recovery:freeze_offer` for the given user. A null/empty userId
 * (unauthenticated, still loading) opens no socket.
 */
export function useRecoveryFreezeOffer(
  userId: string | null | undefined,
): UseRecoveryFreezeOfferResult {
  const [offer, setOffer] = useState<FreezeOffer | null>(null);
  const wsRef = useRef<WebSocket | null>(null);

  const clear = useCallback(() => setOffer(null), []);

  useEffect(() => {
    if (!userId) return;

    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const ws = new WebSocket(`${proto}//${window.location.host}/ws`);
    wsRef.current = ws;

    ws.addEventListener("open", () => {
      // The server registry keys connections by userId; the sessionId field is
      // required by the handshake but only userId routes broadcastToUser events.
      ws.send(JSON.stringify({ type: "auth", userId, sessionId: `freeze-listener-${userId}` }));
    });

    ws.addEventListener("message", (evt) => {
      try {
        const data = JSON.parse(evt.data) as {
          type?: string;
          sessionId?: string;
          startedAt?: string;
          expiresAt?: string;
        };
        if (data.type === "recovery:freeze_offer" && typeof data.sessionId === "string") {
          setOffer({
            sessionId: data.sessionId,
            startedAt: data.startedAt,
            expiresAt: data.expiresAt,
          });
        }
      } catch {
        /* ignore malformed frames */
      }
    });

    return () => {
      try {
        ws.close();
      } catch {
        /* noop */
      }
      wsRef.current = null;
    };
  }, [userId]);

  return { offer, clear };
}
